import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ImageUrlService {

  constructor() { }

  /**
   * Fix malformed image URLs from the API
   * Specifically fixes missing slash between domain and static path
   * 
   * @param url - The image URL to fix
   * @returns Fixed URL or empty string if invalid
   */
  fixImageUrl(url: string): string {
    if (!url || typeof url !== 'string') {
      return '';
    }

    // Fix the missing slash between domain and static path
    if (url.includes('hit2open.comstatic')) {
      return url.replace('hit2open.comstatic', 'hit2open.com/static');
    }

    // Handle other potential URL issues
    if (url.includes('devapi.hit2open.comstatic')) {
      return url.replace('devapi.hit2open.comstatic', 'devapi.hit2open.com/static');
    }

    // Return the URL as-is if no known issues
    return url;
  }

  /**
   * Get the primary image URL from a product's images array
   * 
   * @param product - Product object with images array
   * @returns Fixed primary image URL or empty string
   */
  getProductImageUrl(product: any): string {
    if (!product?.images || product.images.length === 0) {
      return '';
    }

    // Find primary image first
    const primaryImage = product.images.find((img: any) => img.isPrimary);
    if (primaryImage) {
      return this.fixImageUrl(primaryImage.url);
    }

    // Fallback to first image
    return this.fixImageUrl(product.images[0].url);
  }

  /**
   * Get all fixed image URLs from a product
   * 
   * @param product - Product object with images array
   * @returns Array of fixed image URLs
   */
  getProductImageUrls(product: any): string[] {
    if (!product?.images || product.images.length === 0) {
      return [];
    }

    return product.images.map((img: any) => this.fixImageUrl(img.url));
  }

  /**
   * Check if an image URL is valid (not empty and properly formatted)
   * 
   * @param url - Image URL to validate
   * @returns Boolean indicating if URL is valid
   */
  isValidImageUrl(url: string): boolean {
    if (!url || typeof url !== 'string') {
      return false;
    }

    // Basic URL validation
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get a placeholder image URL for products/categories without images
   * 
   * @returns Placeholder image URL or data URI
   */
  getPlaceholderImageUrl(): string {
    // Return a data URI for a simple placeholder
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjhGOUZBIi8+CjxwYXRoIGQ9Ik04MCA2MEg0MFY4MEg4MFY2MFoiIGZpbGw9IiNFOUVDRUYiLz4KPHBhdGggZD0iTTEyMCA4MEg4MFYxMDBIMTIwVjgwWiIgZmlsbD0iI0U5RUNFRiIvPgo8cGF0aCBkPSJNMTYwIDEwMEgxMjBWMTIwSDE2MFYxMDBaIiBmaWxsPSIjRTlFQ0VGIi8+CjxwYXRoIGQ9Ik0xMjAgMTIwSDgwVjE0MEgxMjBWMTIwWiIgZmlsbD0iI0U5RUNFRiIvPgo8cGF0aCBkPSJNODAgMTQwSDQwVjE2MEg4MFYxNDBaIiBmaWxsPSIjRTlFQ0VGIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTEwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNkM3NTdEIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4=';
  }
}